import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import KundeBanner from "../components/KundeBanner";
import { Tilbake, Sidetopp } from "../ny-kundesamhandling/Skjema";
import SesongSmsValg from "../components/dekkhotell/SesongSmsValg";
import { skrivUtEtikett } from "../utils/sykkelhotellEtikett";
import { kundeNavn, fmtDato } from "../constants/dekkhotell";
import {
  STATUS_LABEL,
  STATUS_CLS,
  STED_LABEL,
  HENDELSE_LABEL,
  VARSEL_VIRKEDAGER,
  SESONG_SMS_SPORSMAL,
  sykkelTekst,
} from "../constants/sykkelhotell";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

async function hentJson(url, opts) {
  const r = await fetch(url, opts);
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(data.message || "Noe gikk galt"), { data, status: r.status });
  return data;
}

const post = (url, body) =>
  hentJson(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body || {}) });

const felt = "w-full min-w-0 rounded-xl border-2 border-slate-300 bg-white px-3 py-1.5 focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600/30";
const etikett = "mb-1 block text-sm font-semibold text-slate-800";

function TilLageret({ sh, onFerdig, onAvbryt }) {
  const [plass, setPlass] = useState(sh.plass || "");
  const [feil, setFeil] = useState("");
  async function lagre(e) {
    e.preventDefault();
    setFeil("");
    try {
      onFerdig(await post(`${API_BASE_URL}/sykkelhotell/${sh._id}/til-lageret`, { plass }));
    } catch (err) {
      setFeil(err.message);
    }
  }
  return (
    <form onSubmit={lagre} className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-3 space-y-3">
      <h2 className="text-lg font-bold text-slate-900">Fraktet til lageret</h2>
      <div className="max-w-xs">
        <label htmlFor="sh-plass" className={etikett}>Plass på lageret</label>
        <input id="sh-plass" value={plass} onChange={(e) => setPlass(e.target.value)} placeholder="F.eks. 7" className={felt} />
      </div>
      {feil && <p role="alert" className="text-sm text-red-700">{feil}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className="inline-flex min-h-[2.5rem] items-center gap-2 rounded-xl bg-emerald-700 px-3 py-1.5 font-bold text-white hover:bg-emerald-800 disabled:opacity-60 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50">
          Lagre og skriv ut etikett
        </button>
        <button type="button" onClick={onAvbryt} className="px-4 py-2 rounded-lg border border-slate-300">Avbryt</button>
      </div>
    </form>
  );
}

function BestillHenting({ sh, onFerdig, onAvbryt }) {
  const [sted, setSted] = useState(sh.henting?.sted || sh.innlevertSted || "");
  const [dato, setDato] = useState(sh.henting?.dato || sh.tidligsteHentedato || "");
  const [kommentar, setKommentar] = useState("");
  const [kortVarsel, setKortVarsel] = useState(null); // melding fra serveren når varselet er for kort
  const [feil, setFeil] = useState("");

  async function lagre(e, bekreftKort = false) {
    e?.preventDefault();
    setFeil("");
    try {
      onFerdig(
        await post(`${API_BASE_URL}/sykkelhotell/${sh._id}/bestill-henting`, {
          sted,
          dato,
          kommentar,
          kortVarsel: bekreftKort || undefined,
        })
      );
    } catch (err) {
      if (err.data?.kortVarsel) setKortVarsel(err.message);
      else setFeil(err.message);
    }
  }

  return (
    <form onSubmit={lagre} className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-3 space-y-3">
      <h2 className="text-lg font-bold text-slate-900">Bestill henting</h2>
      <p className="text-sm text-slate-700">
        Kunden sier fra minst {VARSEL_VIRKEDAGER} virkedager før. Tidligste dato med vanlig varsel:{" "}
        <strong>{fmtDato(sh.tidligsteHentedato)}</strong>.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label htmlFor="sh-hent-sted" className={etikett}>Henter hos</label>
          <select id="sh-hent-sted" value={sted} onChange={(e) => setSted(e.target.value)} className={felt}>
            <option value="">Velg sted</option>
            {Object.entries(STED_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="sh-hent-dato" className={etikett}>Dato</label>
          <input
            id="sh-hent-dato"
            type="date"
            value={dato}
            onChange={(e) => {
              setDato(e.target.value);
              setKortVarsel(null);
            }}
            className={felt}
          />
        </div>
        <div>
          <label htmlFor="sh-hent-komm" className={etikett}>Kommentar</label>
          <input id="sh-hent-komm" value={kommentar} onChange={(e) => setKommentar(e.target.value)} className={felt} />
        </div>
      </div>
      {kortVarsel && (
        <div role="alert" className="rounded-lg bg-amber-50 border border-amber-300 p-3 text-sm text-amber-900">
          <p>{kortVarsel}</p>
          <button
            type="button"
            onClick={() => lagre(null, true)}
            className="mt-2 px-3 py-1.5 rounded-lg bg-amber-700 text-white font-medium hover:bg-amber-800"
          >
            Det går, bestill likevel
          </button>
        </div>
      )}
      {feil && <p role="alert" className="text-sm text-red-700">{feil}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className="inline-flex min-h-[2.5rem] items-center gap-2 rounded-xl bg-emerald-700 px-3 py-1.5 font-bold text-white hover:bg-emerald-800 disabled:opacity-60 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50">
          Bestill henting
        </button>
        <button type="button" onClick={onAvbryt} className="px-4 py-2 rounded-lg border border-slate-300">Avbryt</button>
      </div>
    </form>
  );
}

function Klar({ sh, onFerdig, onAvbryt }) {
  const harTelefon = (sh.kunde?.normalizedPhone || "").length >= 8;
  const [sms, setSms] = useState(harTelefon);
  const [feil, setFeil] = useState("");
  const [lagrer, setLagrer] = useState(false);
  async function lagre(e) {
    e.preventDefault();
    setFeil("");
    setLagrer(true);
    try {
      onFerdig(await post(`${API_BASE_URL}/sykkelhotell/${sh._id}/klar`, { sms }));
    } catch (err) {
      setFeil(err.message);
    } finally {
      setLagrer(false);
    }
  }
  return (
    <form onSubmit={lagre} className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-3 space-y-3">
      <h2 className="text-lg font-bold text-slate-900">Klar til henting</h2>
      <p className="text-sm text-slate-700">
        Sykkelen står hos {STED_LABEL[sh.henting?.sted]}. Kunden henter {fmtDato(sh.henting?.dato)}.
      </p>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={sms} disabled={!harTelefon} onChange={(e) => setSms(e.target.checked)} />
        Send SMS til kunden om at sykkelen er klar
        {!harTelefon && <span className="text-slate-600">(mangler telefonnummer)</span>}
      </label>
      {feil && <p role="alert" className="text-sm text-red-700">{feil}</p>}
      <div className="flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={lagrer}
          className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-60"
        >
          {lagrer ? "Lagrer…" : "Registrer som klar"}
        </button>
        <button type="button" onClick={onAvbryt} className="px-4 py-2 rounded-lg border border-slate-300">Avbryt</button>
      </div>
    </form>
  );
}

export default function SykkelhotellDetalj() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sh, setSh] = useState(null);
  const [feil, setFeil] = useState("");
  const [skjema, setSkjema] = useState(""); // "lager" | "henting" | "klar"
  const [felter, setFelter] = useState({ notat: "", medfolger: "", tilstand: "", plass: "" });
  const [sesongSms, setSesongSms] = useState(null);

  const vis = useCallback((p) => {
    setSh(p);
    setFelter({ notat: p.notat || "", medfolger: p.medfolger || "", tilstand: p.tilstand || "", plass: p.plass || "" });
  }, []);

  const hent = useCallback(async () => {
    try {
      const p = await hentJson(`${API_BASE_URL}/sykkelhotell/${id}`);
      vis(p);
      if (p.kunde?._id) {
        const svar = await hentJson(`${API_BASE_URL}/sykkelhotell/sesong-sms/${p.kunde._id}`);
        setSesongSms(svar.verdi);
      }
    } catch (e) {
      setFeil(e.message);
    }
  }, [id, vis]);

  useEffect(() => {
    hent();
  }, [hent]);

  // Gjelder kunden, ikke bare denne posten.
  async function endreSesongSms(verdi) {
    const forrige = sesongSms;
    setSesongSms(verdi);
    try {
      await hentJson(`${API_BASE_URL}/sykkelhotell/sesong-sms/${sh.kunde._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ verdi }),
      });
    } catch (e) {
      setSesongSms(forrige);
      alert(e.message);
    }
  }

  async function patch(endring) {
    try {
      vis(
        await hentJson(`${API_BASE_URL}/sykkelhotell/${id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(endring),
        })
      );
    } catch (e) {
      alert(e.message);
    }
  }

  const lagreFelt = (navn) => () => felter[navn] !== (sh[navn] || "") && patch({ [navn]: felter[navn] });

  async function utlever() {
    if (!window.confirm("Kunden har hentet sykkelen?")) return;
    try {
      vis(await post(`${API_BASE_URL}/sykkelhotell/${id}/utlever`));
    } catch (e) {
      alert(e.message);
    }
  }

  async function slett() {
    if (!window.confirm("Slette feilregistreringen?")) return;
    try {
      await hentJson(`${API_BASE_URL}/sykkelhotell/${id}`, { method: "DELETE" });
      navigate("/sykkelhotell");
    } catch (e) {
      alert(e.message);
    }
  }

  if (feil) return <p role="alert" className="p-4 text-red-700">{feil}</p>;
  if (!sh) return <p className="p-4 text-slate-600">Laster …</p>;

  const knapp = "inline-flex min-h-[2.25rem] items-center gap-1.5 rounded-lg border-2 border-slate-300 bg-white px-3 py-1 text-sm font-semibold text-slate-800 hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50";
  const hoved = "inline-flex min-h-[2.25rem] items-center gap-1.5 rounded-lg bg-emerald-700 px-3 py-1 text-sm font-semibold text-white hover:bg-emerald-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50";
  const ferdig = (etikettOgsa) => (ny) => {
    vis(ny);
    setSkjema("");
    if (etikettOgsa) skrivUtEtikett(ny);
  };
  const veksle = (navn) => () => setSkjema((s) => (s === navn ? "" : navn));

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-10">
      <Tilbake til="/sykkelhotell">Tilbake til sykkelhotellet</Tilbake>
      <Sidetopp
        ikon="hotell"
        farge="lager"
        tittel={<span className="flex flex-wrap items-center gap-2">{sh.nr}
          <span className={`rounded-full px-2.5 py-0.5 text-sm font-semibold ${STATUS_CLS[sh.status]}`}>
            {STATUS_LABEL[sh.status]}
            {sh.status === "utlevert" && ` ${fmtDato(sh.avsluttetDato)}`}
          </span></span>}
        undertittel={`Sykkelhotell · sesong ${sh.sesong}`}
      />
      <KundeBanner
        kundeId={sh.kunde?._id}
        navn={sh.kunde ? kundeNavn(sh.kunde) : "Ukjent kunde"}
        telefon={sh.kunde?.phoneNumber}
        epost={sh.kunde?.email}
      >
        <p className="text-sm text-slate-700">
          {sh.bikeId ? (
            <Link to={`/sykkelkort/${sh.bikeId._id}`} className="font-semibold text-indigo-700 underline underline-offset-2">
              {sykkelTekst(sh)} ({sh.bikeId.shortId})
            </Link>
          ) : (
            sh.sykkel || "Sykkel ikke beskrevet"
          )}
          {sh.elsykkel && (sh.batteriMed ? " · elsykkel, batteriet lagres med" : " · elsykkel, batteriet er ikke med")}
        </p>
        {["henting-bestilt", "klar"].includes(sh.status) && (
          <p className="text-sm font-medium text-sky-900 mt-1">
            Henter {fmtDato(sh.henting?.dato)} hos {STED_LABEL[sh.henting?.sted]}
          </p>
        )}
      </KundeBanner>

      <div className="flex flex-wrap gap-2">
        {sh.status === "innlevert" && (
          <button type="button" onClick={veksle("lager")} className={hoved} aria-expanded={skjema === "lager"}>
            Fraktet til lageret
          </button>
        )}
        {["innlevert", "lagret", "henting-bestilt"].includes(sh.status) && (
          <button
            type="button"
            onClick={veksle("henting")}
            className={sh.status === "lagret" ? hoved : knapp}
            aria-expanded={skjema === "henting"}
          >
            {sh.status === "henting-bestilt" ? "Endre henting" : "Bestill henting"}
          </button>
        )}
        {sh.status === "henting-bestilt" && (
          <button type="button" onClick={veksle("klar")} className={hoved} aria-expanded={skjema === "klar"}>
            Klar til henting
          </button>
        )}
        {["henting-bestilt", "klar"].includes(sh.status) && (
          <button type="button" onClick={utlever} className={sh.status === "klar" ? hoved : knapp}>
            Kunden har hentet
          </button>
        )}
        <button type="button" onClick={() => skrivUtEtikett(sh)} className={knapp}>Skriv ut etikett</button>
        {sh.status === "innlevert" && (
          <button type="button" onClick={slett} className={`${knapp} text-red-700`}>Slett feilregistrering</button>
        )}
      </div>

      {skjema === "lager" && <TilLageret sh={sh} onFerdig={ferdig(true)} onAvbryt={() => setSkjema("")} />}
      {skjema === "henting" && <BestillHenting sh={sh} onFerdig={ferdig(false)} onAvbryt={() => setSkjema("")} />}
      {skjema === "klar" && <Klar sh={sh} onFerdig={ferdig(false)} onAvbryt={() => setSkjema("")} />}

      <div className="rounded-2xl border border-slate-200 bg-white p-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <div>
          <span className="text-slate-600 block">Levert hos</span>
          {STED_LABEL[sh.innlevertSted]}
        </div>
        <div>
          <label htmlFor="sh-d-plass" className="text-slate-600 block">Plass på lageret</label>
          <input
            id="sh-d-plass"
            value={felter.plass}
            disabled={sh.status === "utlevert"}
            onChange={(e) => setFelter((f) => ({ ...f, plass: e.target.value }))}
            onBlur={lagreFelt("plass")}
            className="p-1 border border-slate-300 rounded-lg w-full"
          />
        </div>
        <div>
          <label htmlFor="sh-d-betalt" className="text-slate-600 block">Lagring betalt til</label>
          <input
            id="sh-d-betalt"
            type="date"
            value={sh.betaltTil ? String(sh.betaltTil).slice(0, 10) : ""}
            onChange={(e) => patch({ betaltTil: e.target.value || null })}
            className="p-1 border border-slate-300 rounded-lg"
          />
        </div>
        <div>
          <label htmlFor="sh-d-med" className="text-slate-600 block">Følger med</label>
          <input
            id="sh-d-med"
            value={felter.medfolger}
            onChange={(e) => setFelter((f) => ({ ...f, medfolger: e.target.value }))}
            onBlur={lagreFelt("medfolger")}
            className="p-1 border border-slate-300 rounded-lg w-full"
          />
        </div>
        <div className="md:col-span-2">
          <label htmlFor="sh-d-tilstand" className="text-slate-600 block">Skader og merknader ved innlevering</label>
          <input
            id="sh-d-tilstand"
            value={felter.tilstand}
            onChange={(e) => setFelter((f) => ({ ...f, tilstand: e.target.value }))}
            onBlur={lagreFelt("tilstand")}
            className="p-1 border border-slate-300 rounded-lg w-full"
          />
        </div>
        <div>
          <span className="text-slate-600 block">Siste påminnelse</span>
          {sh.varsel?.sisteDato
            ? `${fmtDato(sh.varsel.sisteDato)} (${sh.varsel.sisteType === "host" ? "høst" : "henting"})`
            : "—"}
        </div>
        <div className="md:col-span-2">
          {sh.kunde?._id && (
            <SesongSmsValg
              idPrefix="sh-detalj"
              tittel="SMS når sykkelhotellet åpner igjen"
              sporsmal={SESONG_SMS_SPORSMAL}
              verdi={sesongSms}
              onChange={endreSesongSms}
              visSporsmal={false}
            />
          )}
        </div>
        <div className="md:col-span-3">
          <label htmlFor="sh-notat" className="text-slate-600 block">Notat</label>
          <textarea
            id="sh-notat"
            rows={2}
            value={felter.notat}
            onChange={(e) => setFelter((f) => ({ ...f, notat: e.target.value }))}
            onBlur={lagreFelt("notat")}
            className="w-full p-2 border border-slate-300 rounded-lg"
          />
        </div>
      </div>

      <section>
        <h2 className="text-lg font-bold text-slate-900 mb-2">Historikk</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-slate-200 rounded-xl text-sm">
            <thead>
              <tr className="bg-slate-100 text-left text-slate-700">
                <th className="px-3 py-2">Dato</th>
                <th className="px-3 py-2">Hva</th>
                <th className="px-3 py-2">Sted / plass</th>
                <th className="px-3 py-2">Ordre</th>
                <th className="px-3 py-2">Av</th>
                <th className="px-3 py-2">Kommentar</th>
              </tr>
            </thead>
            <tbody>
              {[...(sh.hendelser || [])].reverse().map((h, i) => (
                <tr key={i} className="border-t">
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDato(h.dato)}</td>
                  <td className="px-3 py-2">
                    {HENDELSE_LABEL[h.type] || h.type}
                    {h.hentedato && ` til ${fmtDato(h.hentedato)}`}
                  </td>
                  <td className="px-3 py-2">
                    {[STED_LABEL[h.sted], h.plass && `plass ${h.plass}`].filter(Boolean).join(", ")}
                  </td>
                  <td className="px-3 py-2">{h.serviceRef}</td>
                  <td className="px-3 py-2">{h.av}</td>
                  <td className="px-3 py-2">{h.kommentar}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
